/**
 * EditableCell Component
 * Reusable editable cell for Excel-like inline editing
 */

import React, { useState, useEffect, useRef } from 'react';
import './EditableCell.css';

function EditableCell({
  value,
  displayValue,
  onSave,
  type = 'text',
  options = [],
  placeholder = '',
  className = '',
  readOnly = false
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (type === 'text' || type === 'textarea') {
        inputRef.current.select();
      }
    }
  }, [isEditing, type]);

  const handleDoubleClick = () => {
    if (!readOnly) {
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && type !== 'textarea') {
      e.preventDefault();
      handleBlur();
    } else if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
    } else if (e.key === 'Tab') {
      handleBlur();
    }
  };

  const formatDisplayValue = (val) => {
    if (!val) return '';

    if (type === 'date' && val) {
      try {
        return new Date(val).toLocaleDateString();
      } catch (e) {
        return val;
      }
    }

    if (type === 'number' && val) {
      return parseFloat(val).toLocaleString();
    }

    return val;
  };

  // Handle checkbox type
  if (type === 'checkbox') {
    return (
      <td className={`editable-cell ${className}`}>
        <input
          type="checkbox"
          checked={value || false}
          onChange={(e) => onSave(e.target.checked)}
          disabled={readOnly}
          className="editable-cell-checkbox"
        />
      </td>
    );
  }

  // Handle readonly cells
  if (readOnly) {
    return (
      <td className={`editable-cell editable-cell-readonly ${className}`}>
        <div className="editable-cell-display">
          {formatDisplayValue(displayValue || value) || <span className="cell-placeholder">—</span>}
        </div>
      </td>
    );
  }

  if (isEditing) {
    if (type === 'select') {
      return (
        <td className={`editable-cell ${className}`}>
          <select
            ref={inputRef}
            className={`editable-cell-input editable-cell-select ${className}`}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          >
            <option value="">-- Select --</option>
            {options.map((option) => {
              // Support both string arrays and object arrays with value/label
              const optionValue = typeof option === 'object' ? option.value : option;
              const optionLabel = typeof option === 'object' ? option.label : option;
              return (
                <option key={optionValue} value={optionValue}>
                  {optionLabel}
                </option>
              );
            })}
          </select>
        </td>
      );
    }

    if (type === 'textarea') {
      return (
        <td className={`editable-cell ${className}`}>
          <textarea
            ref={inputRef}
            className={`editable-cell-input editable-cell-textarea ${className}`}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            rows={3}
          />
        </td>
      );
    }

    return (
      <td className={`editable-cell ${className}`}>
        <input
          ref={inputRef}
          type={type}
          className={`editable-cell-input ${className}`}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
      </td>
    );
  }

  return (
    <td className={`editable-cell ${className}`}>
      <div
        className="editable-cell-display"
        onDoubleClick={handleDoubleClick}
        title={readOnly ? "This field is read-only" : "Double-click to edit"}
      >
        {formatDisplayValue(displayValue || value) || <span className="cell-placeholder">{placeholder || 'Double-click to edit'}</span>}
      </div>
    </td>
  );
}

export default EditableCell;
